/**
 * Copyright (c) 2025 Robert Schiwon
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy of
 * this software and associated documentation files (the "Software"), to deal in
 * the Software without restriction, including without limitation the rights to
 * use, copy, modify, merge, publish, distribute, sublicense, and/or sell copies of
 * the Software, and to permit persons to whom the Software is furnished to do so,
 * subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS
 * FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR
 * COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER
 * IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN
 * CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */
import { postMessage } from 'util/WorkerUtils';
export default class LocalFileParser {
    #worker;
    #rootUrl;
    #file;
    constructor(worker, rootUrl, file) {
        this.#worker = worker;
        this.#rootUrl = rootUrl;
        this.#file = file;
    }
    get rootUrl() {
        return this.#rootUrl;
    }
    async open() {
        const payload = {
            url: this.#rootUrl.toString(),
            blob: this.#file,
        };
        const openReply = (await postMessage(this.#worker, 'open', payload));
        if (openReply.name !== 'opened') {
            throw new Error(`Could not open file: "${this.#file.name}".`);
        }
    }
    async read(url) {
        const urlString = url.toString();
        if (!urlString.startsWith(this.#rootUrl.toString())) {
            throw new Error(`Illegal URL for parser: ${url}`);
        }
        const payload = { url: url.toString() };
        const response = (await postMessage(this.#worker, 'read', payload));
        const json = response.detail;
        // TODO: harmonize?
        return {
            url: new URL(json.url),
            parameters: json.parameters,
            data: json.data,
            metadata: json.metadata,
            table: json.table,
            childNodeNames: json.childNodeNames,
        };
    }
    async export(format) {
        const payload = { url: this.#rootUrl.toString(), format };
        const response = (await postMessage(this.#worker, 'export', payload));
        const data = response.detail;
        const blob = data.blob;
        return blob;
    }
    async close() {
        const payload = { url: this.#rootUrl.toString() };
        const closeReply = (await postMessage(this.#worker, 'close', payload));
        if (closeReply.name !== 'closed') {
            throw new Error(`Could not close file: "${this.#file.name}."`);
        }
    }
}
